/*
 * ototo-pdp-sticky.js — the sticky buy bar.
 *
 * TWO SURFACES, ONE FILE. sections/ds-pdp.liquid renders one panel and a bar
 * whose product never changes. sections/ds-bestseller.liquid renders six panels
 * in a tablist and ONE bar that follows whichever is selected — the band shows
 * a single panel at a time, so there is only ever one product to buy, which is
 * why "six panels would mean six bars" (the old reason the homepage had none)
 * turned out to be the wrong conclusion from a true premise.
 *
 * ⚠️ IT OWNS NO PRICE AND NO VARIANT. Everything it does is routed through the
 * visible panel's real <form>, which ototo-ds-bestseller.js already keeps in
 * sync with the variant chips and the quantity stepper, and everything it shows
 * is READ BACK OUT of that panel. A second source of truth for the selected
 * variant is how a sticky bar ends up adding the wrong thing.
 *
 * ⚠️ NO WALLET BUTTONS. Shop Pay / Apple Pay / Google Pay live in their own
 * iframes; cloning that markup detaches them and they fail silently in
 * production only. "Buy it now" posts to /cart/add.js and then goes to
 * /checkout — no iframe, so nothing to break.
 */
(function () {
  'use strict';

  var bar = document.querySelector('[data-ot-sticky]');
  if (!bar) return;

  var root = document.querySelector('[data-ot-bs]');
  if (!root) { bar.remove(); return; }

  /* The band re-reads the panel on every tab change; the PDP has one panel and
     server-rendered text that must not be overwritten. */
  var band = bar.hasAttribute('data-ot-sticky-band');

  function panel() {
    /* `:not([hidden])` rather than a remembered reference — the tabs swap the
       attribute, and on the PDP the single panel is never hidden at all. */
    return root.querySelector('[data-ot-bs-panel]:not([hidden])') ||
           root.querySelector('.ot-bs__panel:not([hidden])');
  }

  if (!panel()) { bar.remove(); return; }

  /* ── what the bar says ───────────────────────────────────────────────────
     Band only. The name, the price and the thumbnail all come from the panel,
     so a variant change (which rewrites `.ot-bs__price` in place) and a
     character change are the same event as far as this is concerned.

     ⚠️ READ childNodes[0], NOT textContent, FOR THE PRICE. The price element
     also carries the struck-through compare-at inside an <s>, and textContent
     would put both numbers in the bar — "$19.95$29.95". ds-bestseller.js writes
     the variant price into that same first text node. */
  var elName = bar.querySelector('[data-ot-sticky-name]');
  /* Every price slot: the line under the name AND the one inside "Buy it now"
     (team, 2026-10-12). Both surfaces sync the PRICE — on the PDP too, so a
     variant change reaches the bar; only name/thumbnail/picks are band-only. */
  var elPrices = [].slice.call(bar.querySelectorAll('[data-ot-sticky-price]'));
  var elImg = bar.querySelector('[data-ot-sticky-img]');

  function sync() {
    var p = panel();
    if (!p) return;

    var price = p.querySelector('.ot-bs__price');
    if (price) {
      var first = price.childNodes[0];
      var value = (first && first.nodeValue ? first.nodeValue : price.textContent).trim();
      /* Write only on a change: on the PDP the bar sits INSIDE the observed
         root, so an unconditional write is a mutation that re-runs sync(). */
      if (value) elPrices.forEach(function (el) { if (el.textContent !== value) el.textContent = value; });
    }

    if (!band) return;

    var title = p.querySelector('.ot-bs__title');
    if (elName && title && elName.textContent !== title.textContent.trim()) elName.textContent = title.textContent.trim();

    var shot = p.querySelector('[data-ot-bs-stage] img');
    if (elImg && shot && shot.currentSrc !== elImg.src) {
      elImg.removeAttribute('srcset');
      elImg.src = shot.currentSrc || shot.src;
    }

    var id = p.getAttribute('data-ot-bs-panel');
    bar.querySelectorAll('[data-ot-sticky-pick]').forEach(function (b) {
      b.classList.toggle('is-current', b.getAttribute('data-ot-sticky-pick') === id);
    });
  }

  /* Two triggers, because the two things that change the bar's text change it
     in two different ways: swapping the visible panel is an ATTRIBUTE change,
     and picking a variant rewrites a TEXT NODE inside the panel that is already
     visible. One observer covers both, scoped to the band and to the three
     things that matter. */
  if ('MutationObserver' in window) {
    new MutationObserver(function () { sync(); }).observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['hidden', 'src'],
    });
  }
  sync();

  /* ── show it only once the real buy row is off screen ────────────────────
     ⚠️ THE PANEL IS THE PRIMARY TRIGGER, NOT THE BUY ROW. The obvious test is
     "the buy button scrolled away", and above 900px that never happens on its
     own: the gallery is pinned and `.ot-bs__detail` scrolls INSIDE itself, so
     the buy row stays on screen for as long as the panel does. Watching only
     the buy row left the bar hidden at every scroll position — one of two
     rounds this cost.

     Below 900px nothing is pinned and the buy row DOES leave while the panel is
     still on screen, which on the homepage is most of the band. So both are
     tested and either one showing the bar is enough. Rects rather than an
     IntersectionObserver because the panel being watched is swapped by the
     tablist and the buy row moves inside its own scroller — an observer would
     have to be torn down and re-attached on every tab change.

     ⚠️ TESTING TRAP: the band installs a scroll lock. scrollTo(0, 2000) lands
     around scrollY 69, so any check below roughly 3500 reads as a false
     failure. */
  var ticking = false;
  function measure() {
    ticking = false;
    var p = panel();
    if (!p) return;
    var pr = p.getBoundingClientRect();
    var buy = p.querySelector('.ot-bs__buyrow');
    var br = buy && buy.getBoundingClientRect();
    var show = pr.bottom <= 0 || (br && br.bottom <= 0);
    if (show) bar.removeAttribute('hidden');
    else bar.setAttribute('hidden', '');
  }
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(measure);
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  measure();

  /* ── the picker ──────────────────────────────────────────────────────── */
  var toggle = bar.querySelector('[data-ot-sticky-toggle]');
  var picker = bar.querySelector('[data-ot-sticky-picker]');
  if (toggle && picker) {
    toggle.addEventListener('click', function () {
      var open = picker.hasAttribute('hidden');
      if (open) picker.removeAttribute('hidden');
      else picker.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', String(open));
    });
    /* Clicking anywhere else closes it. */
    document.addEventListener('click', function (e) {
      if (bar.contains(e.target)) return;
      picker.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', 'false');
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      picker.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  /* Band picks select a tab instead of navigating: every character is already
     on this page. The band is scrolled back to rather than left behind — the
     panel that just became visible is above the viewport, and changing what is
     for sale without showing it is how a shopper ends up buying the wrong
     thing. */
  bar.addEventListener('click', function (e) {
    var pick = e.target.closest && e.target.closest('[data-ot-sticky-pick]');
    if (!pick) return;
    var id = pick.getAttribute('data-ot-sticky-pick');
    var tab = root.querySelector('[data-ot-bs-tab="' + id + '"]');
    if (tab) tab.click();
    if (picker) picker.setAttribute('hidden', '');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  function busy(btn, on, label) {
    btn.disabled = on;
    if (on) { btn.dataset.label = btn.textContent; btn.textContent = label; }
    else if (btn.dataset.label) { btn.textContent = btn.dataset.label; }
  }

  /* The form is looked up at CLICK TIME, never cached: on the band the visible
     panel — and with it the form, the variant and the quantity — changes under
     the bar every time a character is picked. */
  function formNow() {
    var p = panel();
    return p && p.querySelector('form[action*="/cart/add"]');
  }

  /* ── add to cart ──────────────────────────────────────────────────────
     Submits the panel's own form so the drawer, the cart count and every
     section listening for a cart change behave exactly as they do from the
     real button. `requestSubmit` runs validation and fires `submit`, which
     `.click()` on a detached button would not. */
  var add = bar.querySelector('[data-ot-sticky-add]');
  if (add) {
    add.addEventListener('click', function () {
      var form = formNow();
      if (!form) return;
      var submit = form.querySelector('[type="submit"]');
      if (form.requestSubmit) form.requestSubmit(submit || undefined);
      else form.submit();
    });
  }

  /* ── buy it now ───────────────────────────────────────────────────────
     Adds through the AJAX endpoint and then goes to checkout. Reading the
     form means the chosen variant and quantity come along. */
  var buy = bar.querySelector('[data-ot-sticky-buy]');
  if (buy) {
    buy.addEventListener('click', function () {
      var form = formNow();
      if (!form) return;
      busy(buy, true, 'Adding…');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(new FormData(form)).toString(),
      })
        .then(function (r) {
          if (!r.ok) throw new Error(r.status);
          window.location.href = '/checkout';
        })
        .catch(function () {
          /* Sold out, or the connection went. Fall back to the cart rather
             than stranding the shopper on a dead button. */
          busy(buy, false);
          window.location.href = '/cart';
        });
    });
  }
})();
